/**
 * The player's Favorites and recently played games. It lives only in this browser (localStorage): no account, nothing sent
 * anywhere. Components read it with useLibrary(), which updates live.
 */
import { useSyncExternalStore } from 'react';
import { readJson, writeJson } from './storage';

const FAV_KEY = 'sf:favorites';
const RECENT_KEY = 'sf:recent';
/** How many recently played games to keep (the homepage's Jump Back In row). */
const RECENT_MAX = 12;
const EVENT = 'sf:library';

interface Library {
  favorites: string[];
  /** Most recently played first. */
  recent: string[];
}

const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

let cache: Library | null = null;
function read(): Library {
  cache ??= { favorites: ids(readJson(FAV_KEY, [])), recent: ids(readJson(RECENT_KEY, [])) };
  return cache;
}

function save(next: Library): void {
  cache = next;
  writeJson(FAV_KEY, next.favorites);
  writeJson(RECENT_KEY, next.recent);
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    // Another tab changed the lists.
    if (e.key === FAV_KEY || e.key === RECENT_KEY) {
      cache = null;
      onChange();
    }
  };
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

const EMPTY: Library = { favorites: [], recent: [] };
export const useLibrary = (): Library => useSyncExternalStore(subscribe, read, () => EMPTY);

export function toggleFavorite(id: string): void {
  const lib = read();
  const favorites = lib.favorites.includes(id) ? lib.favorites.filter((x) => x !== id) : [id, ...lib.favorites];
  save({ ...lib, favorites });
}


/** Moves a game to the front of the recently played list (called when its Play button is pressed). */
export function markPlayed(id: string): void {
  const lib = read();
  if (lib.recent[0] === id) return;
  save({ ...lib, recent: [id, ...lib.recent.filter((x) => x !== id)].slice(0, RECENT_MAX) });
}
